import { IObjectsDrawer, IObjectsPool, IMapMngr } from './interfaces';
import { DisplayObject } from './DisplayObject';
import * as aspSettings from './aspSettings';
import * as settings from '../common/settings';
import * as utils from '../common/utils';
import { ObjectsPoolSubscriber } from './ObjectsPool';
import { AspObject } from './AspObject';
import { SensorObject } from 'src/app/asp/SensorObject';

const MODELS_3D_PATH = '/assets/Images/3D/';

class DisplayObjectsSlot {
  objects: Array<DisplayObject> = new Array();
  visible: boolean = false;
}

export abstract class ObjectsDrawer implements IObjectsDrawer, ObjectsPoolSubscriber {

  protected initialized = false;
  protected objectsType: string;
  protected layerId: string;
  public objectsLayer: NeWMI.Layer.DrawMgrLayer;
  protected objectsLayerVisible: boolean;
  drawMgr: any;
  protected allObjects = new Map();

  protected objectsSlots: Array<DisplayObjectsSlot> = null;

  // manage a buffer to add objects to DM upon periodic activities
  protected addToDmBuffer = new Map();
  protected addToDmPeriodic = false;

  protected dataKeys = [];
  protected typeKeys = [];

  protected largeTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.LARGE_FONT, charsRanges: [[32, 9000]] });
  protected normalTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.NORMAL_FONT, charsRanges: [[32, 9000]] });
  protected smallTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.SMALL_FONT, charsRanges: [[32, 9000]] });

  protected large3DTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.LARGE_FONT });
  protected normal3DTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.NORMAL_FONT });
  protected small3DTextStyle = new NeWMI.Draw.Styles.FontStyle({ font: aspSettings.SMALL_FONT });

  protected globalParams = { color: [1, 1, 1, 1], factor: 1 };

  static initImagesToLoad(svgSymbols: string[], svgObjectSymbols?: string[], pngSymbols?: string[]) {
    let images = [];

    if (svgSymbols)
      for (let symbol of svgSymbols)
        images.push(`/assets/Images/${symbol}.svg`);
    if (pngSymbols)
      for (let symbol of pngSymbols)
        images.push(`/assets/Images/${symbol}.png`);
    if (svgObjectSymbols)
      for (let symbol of svgObjectSymbols)
        images.push(`/assets/Images/Objects/${symbol}.svg`);

    return images;
  }

  constructor(protected mapMngr: IMapMngr, protected objectsPool: IObjectsPool, protected filterType: settings.DisplayFilter,
    protected parentLayer, protected objectTemplate,
    protected imagesToLoad, protected drawFlags: Array<any>, protected itemsProperties,
    globalParams?, protected models3D?: Array<any>) {

    this.objectsType = objectsPool.getObjectType();
    this.layerId = objectsPool.getObjectType() + '-layer';

    this.dataKeys.push(objectsPool.getObjectType());
    this.typeKeys.push(this.objectsType);

    if (globalParams)
      this.globalParams = globalParams;

    if (this.models3D) {
      for (let model3D of this.models3D) {
        if (model3D.model)
          model3D.model = MODELS_3D_PATH + model3D.model;
        if (model3D.material)
          model3D.material = MODELS_3D_PATH + model3D.material;
      }
    }

    if (this.imagesToLoad) {
      let _this = this;
      NeWMI.Draw.Styles.ImageStyle.loadImages({ urls: this.imagesToLoad }).then(function (imagesStyle) {
        let myImageStyle = imagesStyle;
        _this.allImagesLoaded(myImageStyle);
      });
    }

    this.objectsLayerVisible = this.isLayerVisible();

    this.objectsLayer = new NeWMI.Layer.DrawMgrLayer(utils.isWebGLSupported(), this.is3D());
    this.objectsLayer.newmiProps.p_objID = this.layerId;

    if (this.objectsPool.hookable() || this.objectsPool.clickable())
      this.objectsLayer.setEnableAutoIndex({ enabled: true, returnExtendedObjects: false /* TBD: true */ });
    this.objectsLayer.newmiProps.set_visible(this.objectsLayerVisible);

    this.drawMgr = this.objectsLayer.createNewDrawMgr({ map: mapMngr.getMap() });

    this.parentLayer.insert(this.objectsLayer);

    let slotsCount = objectsPool.getObjectsSlotsCount();
    if (slotsCount > 0) {
      this.objectsSlots = new Array(slotsCount);
      for (let i = 0; i < slotsCount; i++)
        this.objectsSlots[i] = new DisplayObjectsSlot();
    }

    if (!this.imagesToLoad)
      this.allImagesLoaded(null);
  }

  is3D(): boolean {
    return this.mapMngr.is3D();
  }

  drawUsingTemplate(): boolean {
    return this.objectTemplate;
  }

  drawUsing3DModel(): boolean {
    return this.models3D && this.models3D.length > 0;
  }

  protected templateStyles(images): any[] {
    let style = new NeWMI.Draw.Styles.ImagesFontStyle({ images: images });
    return [style];
  }

  isLayerVisible(): boolean {
    if (this.filterType)
      return settings.userSettings.filterOn[this.filterType];
    else
      return true;
  }

  protected addDrawTypes(images) {
    if (this.objectTemplate) {
      if (this.is3D() && this.drawUsing3DModel()) {
        this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.Model3D, this.objectsType, { models: this.models3D });
      }
      else {
        let drawFlags = this.is3D() ?
          [{ scale: Number.MAX_VALUE, flags: new Array<boolean>(this.drawFlags[0].flags.length).fill(true) }] :
          this.drawFlags;
        this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.Template, this.objectsType,
          {
            template: this.objectTemplate,
            styles: this.templateStyles(images),
            drawFlags: drawFlags,
            itemsProperties: this.itemsProperties,
            properties: NeWMI.Draw.DrawMgr.Enums.EProperties.GFactor,
            globalParams: this.globalParams,
            supportPicking: this.objectsPool.supportPicking(),
            autoIndex: (this.objectsPool.hookable() || this.objectsPool.clickable()) ? { enabled: true, selectable: this.objectsPool.hookable() } : undefined,
            supportCluster: true,
          });
      }
    }
  }

  // Called after all images are loaded by NeWMI
  protected allImagesLoaded(images) {
    this.addDrawTypes(images);

    this.objectsLayer.drawSteps.push({
      dmsData: [
        { drawMgr: this.drawMgr, visible: true, isDrawn: true }
      ]
    });

    this.initialized = true;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getObject(objectId): DisplayObject {
    let obj = this.allObjects.get(objectId);
    return obj === undefined ? null : obj;
  }

  getObjectsPool(): IObjectsPool {
    return this.objectsPool;
  }

  getMapMngr(): IMapMngr {
    return this.mapMngr;
  }

  protected addObject(obj: DisplayObject) {
    this.allObjects.set(obj.id, obj);
    if (this.objectsSlots)
      this.objectsSlots[obj.getAspObject().slotNumber].objects.push(obj);
  }

  protected deleteObject(obj: DisplayObject) {
    this.removeObjectFromDm(obj);
    this.allObjects.delete(obj.id);
    if (this.mapMngr.getDynamicCenterId() == obj.id)
      this.mapMngr.setDynamicCenterId(null);
  }

  // Periodic refresh, defined in IObjectsPool
  periodicUpdate(): boolean {
    if (!this.initialized || !this.objectsLayer)
      return false;

    if (this.objectTemplate) {
      // if check box selected...
      if (settings.userSettings.autoSymbolSize)
        this.drawMgr.typesVisualProps[this.objectsType].globalParams.factor = this.getMapMngr().getSizeByZoomValue();
      else
        this.drawMgr.typesVisualProps[this.objectsType].globalParams.factor = settings.userSettings.symbolsSize;
    }

    this.objectsLayer.opacity = this.getOpacity();
    let visible = this.isLayerVisible();

    if (visible) {
      this.allObjects.forEach((obj) => {
        obj.prepareToDraw();
      });
    }

    if (visible != this.objectsLayerVisible) {
      this.objectsLayerVisible = visible;
      this.objectsLayer.newmiProps.set_visible(visible);
    }

    if (this.objectsSlots) {
      // show/hide all relevant slots
      for (let slot = 0; slot < this.objectsSlots.length; slot++) {
        if (slot != this.objectsPool.getCurrentSlot()) {
          let visible = this.isSlotVisible(slot);
          if (visible != this.objectsSlots[slot].visible) {
            if (visible)
              this.showSlotObjects(slot);
            else
              this.hideSlotObjects(slot);
            this.objectsSlots[slot].visible = visible;
          }
        }
      }
    }

    if (this.addToDmPeriodic && this.drawMgr && visible) {
      let added: Set<any> = new Set<any>();
      this.addToDmBuffer.forEach((obj: DisplayObject) => {
        if (obj.isVisible() && obj.getAspObject().isValid()) {
          this.addObjectToDm(obj);
          added.add(obj.id);
        }
      });
      added.forEach((id) => {
        this.addToDmBuffer.delete(id);
      });
    }

    return true;
  }

  deleteAllObjects() {
    if (this.objectsSlots != null) {
      for (let i = 0; i < this.objectsSlots.length; i++)
        this.onDeleteSlot(i);
    }
    else {
      this.allObjects.forEach((obj: DisplayObject) => {
        this.removeObjectFromDm(obj);
      });
      this.allObjects.clear();
    }
    if (this.addToDmPeriodic)
      this.addToDmBuffer.clear();
  }

  getLayer(): NeWMI.Layer.DrawMgrLayer {
    return this.objectsLayer;
  }

  protected getSlotsHistory() {
    return 0;
  }

  protected isSlotVisible(slotNumber: number): boolean {
    if (!this.isLayerVisible())
      return false;
    let slotAge = this.objectsPool.getSlotAge(slotNumber);
    if (slotAge === undefined)
      return false;
    return slotAge <= this.getSlotsHistory();
  }

  protected showSlotObjects(slotNumber: number) {
    for (let obj of this.objectsSlots[slotNumber].objects) {
      if (obj.isVisible())
        this.addObjectToDm(obj);
    }
  }

  protected hideSlotObjects(slotNumber: number) {
    for (let obj of this.objectsSlots[slotNumber].objects)
      this.removeObjectFromDm(obj);
  }

  addObjectToDm(obj: DisplayObject, dataKeys?, typeKeys?) {
    this.drawMgr.addObject({ object: obj, dataKeys: dataKeys ? dataKeys : this.dataKeys, typeKeys: typeKeys ? typeKeys : this.typeKeys });
    if (typeof dataKeys == 'undefined' && typeof typeKeys == 'undefined')
      obj.isDisplayed = true;
  }

  updateObjectInDm(obj: DisplayObject, dataKeys?, typeKeys?) {
    // this.drawMgr.updateObject({ object: obj });
    this.drawMgr.updateObject({ object: obj, dataKeys: dataKeys ? dataKeys : this.dataKeys, typeKeys: typeKeys ? typeKeys : this.typeKeys });

  }

  removeObjectFromDm(obj: DisplayObject, dataKeys?, typeKeys?) {
    if (dataKeys && typeKeys)
      this.drawMgr.removeObject({ object: obj, dataKeys: dataKeys ? dataKeys : this.dataKeys, typeKeys: typeKeys ? typeKeys : this.typeKeys });
    else
      this.drawMgr.removeObject({ object: obj });
    obj.isDisplayed = false;
  }

  // Return visible objects count
  visibleCount(): number {
    let count = 0;
    if (this.objectsLayer && this.objectsLayer.newmiProps.visible) {
      const mapExtent = settings.userSettings.mapExtent;
      const searchGeo = new NeWMI.Geometry.Rectangle({
        xCenter: mapExtent.xCenter, yCenter: mapExtent.yCenter,
        width: mapExtent.width, height: mapExtent.height
      });
      const foundObjects = this.objectsLayer.search(searchGeo);
      count = foundObjects.length;
    }
    return count;
  }

  onObjectCreate(obj: AspObject): DisplayObject {
    let dispObj = this.createDisplayObject(obj);
    dispObj.onDataChange();
    this.addObject(dispObj);
    if (this.addToDmPeriodic)
      this.addToDmBuffer.set(obj.id, dispObj);
    return dispObj;
  }

  onObjectDelete(obj: AspObject): void {
    if (this.objectsSlots) {
      // if using slots, an entire slot is delete at once, using onDeleteSlot()
    }
    else {
      let dispObj = this.getObject(obj.id);
      if (dispObj)
        this.deleteObject(dispObj);
    }
    if (this.addToDmPeriodic) {
      if (this.addToDmBuffer.has(obj.id))
        this.addToDmBuffer.delete(obj.id);
    }
  }

  onObjectHook(obj: AspObject): void {
    let dispObj = this.getObject(obj.id);
    if (dispObj) {
      let objs = [];
      objs.push(dispObj);
      this.objectsLayer.setSelection(objs);
    }
  }

  onDeleteAllObjects(): void {
    this.deleteAllObjects();
  }

  onDeleteSlot(slotNumber: number): void {
    if (this.objectsSlots && slotNumber < this.objectsSlots.length) {
      for (let dispObj of this.objectsSlots[slotNumber].objects)
        this.deleteObject(dispObj);
      this.objectsSlots[slotNumber].objects.length = 0;
      this.objectsSlots[slotNumber].visible = false;
    }
  }

  subscribeObjectsPool() {
    this.objectsPool.addSubscriber(this);
  }

  unsubscribeObjectsPool() {
    this.objectsPool.delSubscriber(this);
  }

  createObjectsFromPool() {
    let poolObjects = this.objectsPool.getAllObjects();
    poolObjects.forEach((obj: AspObject) => {
      let dispObj = this.onObjectCreate(obj);
      dispObj.onDataChange();
    });
  }

  handleUserAction(action: number, obj: AspObject): boolean {
    return false;
  }

  getOpacity(): number {
    return 1;
  }

  protected addGeometryDrawType(type: any, lineProps = {}, autoIndex = {}) {
    this.drawMgr.addType(
      this.is3D() ? NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry3D : NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry,
      type,
      this.is3D() ? lineProps : { line: lineProps, autoIndex: autoIndex }
    );
  }

  protected addFilledGeometryDrawType(type: any, lineProps = {}, fillProps = {}, autoIndex = {}) {
    this.drawMgr.addType(
      this.is3D() ? NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry3D : NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry,
      type,
      this.is3D() ? lineProps : { line: lineProps, fill: fillProps, autoIndex: autoIndex }
    );
  }

  abstract createDisplayObject(obj: AspObject): DisplayObject;
}
